import { useConvertCurrency } from '@utils/currencyConverter';
import { getCountryByCurrency } from '@utils/locations';

import { COUNTRIES } from '../constants/locations';
import { CountryCode, CurrencyCode } from '../types/Locations';
import { formatPrice } from '../utils/prices';
import { useCountryCode } from './useCountryCode';
import { useTaxPricing } from './useTaxPricing';

type Params = {
  price: number;
  countryCode?: CountryCode;
  currencyCode?: CurrencyCode;
  inMinorUnit?: boolean;
  skipConvertCurrency?: boolean;
  skipTaxCalculation?: boolean;
  prefix?: string;
  suffix?: string;
};

export const useProductPrice = ({
  countryCode,
  currencyCode,
  inMinorUnit = false,
  prefix,
  price,
  skipConvertCurrency = false,
  skipTaxCalculation = false,
  suffix,
}: Params): string => {
  const [userCountryCode] = useCountryCode();
  const { calculateTaxPricing } = useTaxPricing({
    countryCode: countryCode || userCountryCode,
  });
  const { convert } = useConvertCurrency();

  const countryByCurrency = currencyCode
    ? getCountryByCurrency(currencyCode)
    : null;
  const { currency, minorUnit, roundingFn } =
    COUNTRIES[countryByCurrency || countryCode || userCountryCode];

  const priceMinor = inMinorUnit ? price / minorUnit : price;
  const priceInSelectedCurrency = skipConvertCurrency
    ? priceMinor
    : convert(priceMinor, currency);
  const priceRounded = roundingFn
    ? roundingFn(priceInSelectedCurrency)
    : Math.round(priceInSelectedCurrency);
  const priceWithTaxes = skipTaxCalculation
    ? priceRounded
    : calculateTaxPricing(priceRounded);
  const pricing = String(formatPrice(priceWithTaxes, currency));

  if (prefix) return `${prefix} ${pricing}`;
  if (suffix) return `${pricing} ${suffix}`;
  return pricing;
};
